"use client";

/**
 * Reviews Section V2 - Enhanced with service information
 */

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { formatRelativeTime } from "@/lib/tenant-landing-v2-utils";
import type { EnhancedReview, ReviewsData } from "@/types/tenant-landing-v2";
import { ChevronLeft, ChevronRight, Star, TrendingUp } from "lucide-react";
import { useState } from "react";
import { Separator } from "../ui/separator";

interface ReviewsV2Props {
  reviewsData: ReviewsData | null;
  tenantName: string;
  onPageChange?: (page: number) => void;
  loading?: boolean;
}

export function ReviewsV2({
  reviewsData,
  tenantName,
  onPageChange,
  loading = false,
}: ReviewsV2Props) {
  const [currentPage, setCurrentPage] = useState(1);

  if (!reviewsData || reviewsData.stats.totalReviews === 0) {
    return null;
  }

  const { reviews, pagination, stats } = reviewsData;

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    onPageChange?.(newPage);
    // Scroll to section
    const section = document.getElementById("avaliacoes");
    section?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const getDistributionPercentage = (count: number) => {
    if (stats.totalReviews === 0) return 0;
    return (count / stats.totalReviews) * 100;
  };

  return (
    <section
      id="avaliacoes"
      className="py-4 bg-zinc-50 dark:bg-zinc-950 scroll-mt-16"
    >
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 px-4 py-2 rounded-full mb-4">
            <Star className="h-4 w-4 fill-current" />
            <span className="text-sm font-semibold">
              Avaliações dos Clientes
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            O que nossos clientes dizem
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Confira as experiências de quem já foi atendido por{" "}
            <span className="font-semibold text-foreground">{tenantName}</span>
          </p>
        </div>

        <Card className="max-w-4xl mx-auto mb-4 md:mb-12 shadow-lg">
          <CardContent className="p-6 md:p-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="text-center">
                <div className="text-6xl md:text-7xl font-bold text-foreground mb-3">
                  {stats.averageRating.toFixed(1)}
                </div>
                <div className="flex justify-center mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`h-6 w-6 ${
                        i < Math.round(stats.averageRating)
                          ? "text-yellow-400 fill-yellow-400"
                          : "text-gray-300"
                      }`}
                    />
                  ))}
                </div>
                <p className="text-sm text-muted-foreground">
                  Baseado em{" "}
                  <span className="font-semibold text-foreground">
                    {stats.totalReviews}
                  </span>{" "}
                  {stats.totalReviews === 1 ? "avaliação" : "avaliações"}
                </p>
              </div>

              <div className="space-y-3">
                {[5, 4, 3, 2, 1].map((star) => {
                  const count =
                    stats.distribution[star as keyof typeof stats.distribution];
                  const percentage = getDistributionPercentage(count);

                  return (
                    <div key={star} className="flex items-center gap-3">
                      <div className="flex items-center gap-1 w-16 shrink-0">
                        <span className="text-sm font-medium">{star}</span>
                        <Star className="h-3 w-3 text-yellow-400 fill-yellow-400" />
                      </div>
                      <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                        <div
                          className="bg-yellow-400 h-full transition-all duration-500 ease-out"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="text-sm text-muted-foreground w-12 text-right">
                        {count}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {stats.averageRating >= 4.5 && (
              <div className="mt-6 pt-6 border-t">
                <div className="flex items-center justify-center gap-2 text-green-600 dark:text-green-400">
                  <TrendingUp className="h-5 w-5" />
                  <span className="font-semibold">Excelente Avaliação!</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {reviews.map((review, index) => (
            <ReviewCard
              key={review.id}
              review={review}
              index={index}
              loading={loading}
            />
          ))}
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1 || loading}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <div className="flex items-center gap-2">
              {[...Array(pagination.totalPages)].map((_, i) => {
                const pageNum = i + 1;
                const isCurrentPage = pageNum === currentPage;

                // Show first, last, and pages around current
                const shouldShow =
                  pageNum === 1 ||
                  pageNum === pagination.totalPages ||
                  (pageNum >= currentPage - 1 && pageNum <= currentPage + 1);

                if (!shouldShow) {
                  if (pageNum === 2 || pageNum === pagination.totalPages - 1) {
                    return <span key={pageNum}>...</span>;
                  }
                  return null;
                }

                return (
                  <Button
                    key={pageNum}
                    variant={isCurrentPage ? "default" : "outline"}
                    size="sm"
                    onClick={() => handlePageChange(pageNum)}
                    disabled={loading}
                    className="min-w-[40px]"
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>

            <Button
              variant="outline"
              size="icon"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === pagination.totalPages || loading}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}

interface ReviewCardProps {
  review: EnhancedReview;
  index: number;
  loading?: boolean;
}

function ReviewCard({ review, index, loading }: ReviewCardProps) {
  const initials = review.customerName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <Card
      className={`hover:shadow-lg transition-all duration-300 animate-in fade-in slide-in-from-bottom-4 ${
        loading ? "opacity-50" : ""
      }`}
      style={{
        animationDelay: `${index * 100}ms`,
        animationFillMode: "backwards",
      }}
    >
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 border-2 border-primary/20">
              <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-semibold text-sm">{review.customerName}</p>
              <p className="text-xs text-muted-foreground">
                {formatRelativeTime(review.createdAt)}
              </p>
            </div>
          </div>

          <div className="flex shrink-0">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className={`h-4 w-4 ${
                  i < review.rating
                    ? "text-yellow-400 fill-yellow-400"
                    : "text-gray-300"
                }`}
              />
            ))}
          </div>
        </div>

        {review.comment && (
          <div className="bg-zinc-800 rounded-md p-2">
            <p className="text-sm text-muted-foreground leading-relaxed">
              {review.comment}
            </p>
          </div>
        )}

        {review.services.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {review.services.map((service) => (
              <Badge
                key={service.id}
                variant="secondary"
                className="text-xs font-normal"
              >
                {service.name}
              </Badge>
            ))}
          </div>
        )}

        {review.response && (
          <div className="border-l-2 pl-4 space-y-2">
            <Separator />
            <p className="text-xs font-semibold text-primary">
              Resposta do estabelecimento
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {review.response}
            </p>
            {review.responseAt && (
              <p className="text-xs text-muted-foreground">
                {formatRelativeTime(review.responseAt)}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
